import { useEffect, useState } from "react";
import { getJSON, postJSON, putJSON } from "../services/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "ccf4a2fb-4ccb-4292-ba83-6c565146e4f0", // Users - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
  "c7b622a4-bdb6-48b4-b39e-9f0970a9602f", // CEP - Users
];

// --- Tipos
type Equipamento = {
  cd_origem: number;
  de_equipamento: string;
  de_observacoes?: string;
  fg_ativo: number;
  motivo?: string;
  data_inicio?: string; // ISO
  data_fim?: string;    // ISO
  usuario?: string;
};

type Motivo = { id_motivo: number; descricao: string; ativo: string };

export default function EquipamentosPage() {
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>([]);
  const [loading, setLoading] = useState(false);

  // Estados de modal
  const [editando, setEditando] = useState<Equipamento | null>(null);
  const [inativando, setInativando] = useState<Equipamento | null>(null);
  const [reativando, setReativando] = useState<Equipamento | null>(null);

  // Formulários
  const [nome, setNome] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const [motivoId, setMotivoId] = useState("");
  const [observacaoInativar, setObservacaoInativar] = useState("");

  async function carregar() {
    setLoading(true);
    try {
      const data = await getJSON("/equipamentos");
      setEquipamentos(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  // --- Editar
  function abrirEditar(eq: Equipamento) {
    setEditando(eq);
    setNome(eq.de_equipamento);
    setObservacoes(eq.de_observacoes ?? "");
  }

  async function salvarEdicao() {
    if (!editando) return;

    if (editando.cd_origem === 0) {
      // 👇 Cadastro novo
      await postJSON("/equipamentos", {
        nome,
        observacoes,
      });
    } else {
      // 👇 Atualização
      await putJSON(`/equipamentos/${editando.cd_origem}`, {
        nome,
        observacoes,
      });
    }

    setEditando(null);
    carregar();
  }


  // --- Inativar
  async function abrirInativar(eq: Equipamento) {
    setInativando(eq);
    setMotivoId("");
    setObservacaoInativar("");
    const data = await getJSON("/motivos");
    setMotivos(Array.isArray(data) ? data : []);
  }

  async function confirmarInativar() {
    if (!inativando) return;
    await putJSON(`/equipamentos/${inativando.cd_origem}/inativar`, {
      motivo_id: motivoId,
      observacao: observacaoInativar,
    });
    setInativando(null);
    carregar();
  }

  // --- Reativar
  function abrirReativar(eq: Equipamento) {
    setReativando(eq);
  }

  async function confirmarReativar() {
    if (!reativando) return;
    await putJSON(`/equipamentos/${reativando.cd_origem}/reativar`, {});
    setReativando(null);
    carregar();
  }

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold">Gestão de Equipamentos</h1>
        <Button
          onClick={() => setEditando({ cd_origem: 0, de_equipamento: "", fg_ativo: 1 })}
        >
          + Novo Bico
        </Button>
      </div>

      {loading ? (
        <div>Carregando...</div>
      ) : (
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left">Observações</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {equipamentos.map((eq) => (
              <tr key={eq.cd_origem} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-800">
                  {eq.de_equipamento}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {eq.de_observacoes || "-"}
                </td>
                <td className="px-4 py-3 text-center">
                  {eq.fg_ativo ? (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                      Ativo
                    </span>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                        Inativo
                      </span>
                      {/* Só mostra detalhes quando está inativo */}
                      <div className="text-xs text-slate-500 text-left">
                        {eq.motivo && (
                          <>
                            Motivo: {eq.motivo} <br />
                          </>
                        )}
                        {eq.data_inicio && (
                          <>Desde: {new Date(eq.data_inicio).toLocaleString()}<br /></>
                        )}
                        {eq.usuario && <>Por: {eq.usuario}</>}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 flex justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => abrirEditar(eq)}
                    className="text-xs"
                  >
                    ✏️ Editar
                  </Button>
                  {eq.fg_ativo ? (
                    <Button
                      variant="destructive"
                      onClick={() => abrirInativar(eq)}
                      className="text-xs"
                    >
                      ⛔ Inativar
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => abrirReativar(eq)}
                      className="text-xs"
                    >
                      ✅ Reativar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

      )}

      {/* --- Modal Editar / Novo --- */}
      <Dialog open={!!editando} onOpenChange={() => setEditando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editando?.cd_origem === 0 ? "Novo Equipamento" : "Editar Equipamento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              className="w-full border rounded px-2 py-1"
              placeholder="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
            <textarea
              className="w-full border rounded px-2 py-1"
              placeholder="Observações"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
            />
            <Button onClick={salvarEdicao}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Modal Inativar --- */}
      <Dialog open={!!inativando} onOpenChange={() => setInativando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Inativar {inativando?.de_equipamento ?? "Equipamento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <select
              className="w-full border rounded px-2 py-1"
              value={motivoId}
              onChange={(e) => setMotivoId(e.target.value)}
            >
              <option value="">Selecione um motivo</option>
              {motivos.map((m) => (
                <option key={m.id_motivo} value={m.id_motivo}>
                  {m.descricao}
                </option>
              ))}
            </select>
            <textarea
              placeholder="Observação (opcional)"
              className="w-full border rounded px-2 py-1"
              value={observacaoInativar}
              onChange={(e) => setObservacaoInativar(e.target.value)}
            />
            <Button
              onClick={confirmarInativar}
              disabled={!motivoId}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirmar Inativação
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Dialog Reativar --- */}
      <AlertDialog open={!!reativando} onOpenChange={() => setReativando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar reativação de {reativando?.de_equipamento ?? "Equipamento"}?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReativando(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarReativar}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Reativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </GroupGuard>
  );
}
